import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import {request} from '../request';
import {AnalyticsParametersPrompts} from '../../types/prompts/AnalyticsParameters';

/**
 * This file contains the calls to retrieve analytic parameters
 * associated with an analytics-supported application.
 */
export type DeploymentAnalyticParametersRequest = {
  latest?: boolean;
  projectId?: string;
  applicationId?: string;
};

export type DeploymentAnalyticParametersResponse = AnalyticsParametersPrompts;

export const AnalyticParametersPath = '/analytics/parameterDefs';

/**
 * This call retrieves analytic parameters for a deployment.
 */
export async function getDeploymentAnalyticParameters(
  params: DeploymentAnalyticParametersRequest
) {
  const response = await request<DeploymentAnalyticParametersResponse>({
    params,
    method: 'get',
    url: AnalyticParametersPath,
  });

  return response.body;
}

type UseDeploymentAnalyticParameters = DeploymentAnalyticParametersRequest & {
  avoidFetch?: boolean;
};

export function useDeploymentAnalyticParameters({
  avoidFetch = false,
  ...data
}: UseDeploymentAnalyticParameters) {
  const shouldFetch = !avoidFetch && data.projectId && data.applicationId;

  return useSWR(
    shouldFetch
      ? `${AnalyticParametersPath}-latest-${data.latest}-${data.projectId}-${data.applicationId}`
      : null,
    async () => await getDeploymentAnalyticParameters(data),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
}

export function useDeploymentDefaultEnvironmentParameters({
  avoidFetch = false,
  ...data
}: UseDeploymentAnalyticParameters) {
  const shouldFetch = !avoidFetch && data.projectId && data.applicationId;

  return useSWRImmutable(
    shouldFetch ? `${AnalyticParametersPath}/default-parameters-${data.projectId}` : null,
    async () =>
      await getDeploymentAnalyticParameters({
        ...data,
        latest: false,
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
}
